/**
 * Booking Items Editor
 * View, add, and remove booking items
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Trash2, AlertTriangle, Plus, ArrowRightLeft, Truck, Clock } from 'lucide-react';
import AddItemsModal from './AddItemsModal';
import ChangeAllocationModal from './ChangeAllocationModal';
import ReportIssueModal from './ReportIssueModal';
import { formatCurrency } from '../lib/currency';
import { useToast } from '../contexts/ToastContext';
import type { Database } from '../lib/database.types';
import type { ItemPricingRuleColumns } from '../services/pricing.service';

type Booking = Database['public']['Tables']['bookings']['Row'];
type BookingItem = Database['public']['Tables']['booking_items']['Row'];
type BookingExtension = Database['public']['Tables']['booking_extensions']['Row'];

interface BookingItemWithRelations extends BookingItem {
  product_types?: { product_name?: string; product_type?: string; location_id?: string; locations?: { location_name: string } | null };
  inventory_items?: { nr?: number; item_number?: string; combination_code?: string; product_type_id?: string; product_types?: { location_id?: string; locations?: { location_name: string } | null } };
}

interface BookingItemsEditorProps {
  booking: Booking;
  items: BookingItemWithRelations[];
  extensions?: readonly BookingExtension[];
  onChange: (items: BookingItem[]) => void;
  disabled: boolean;
  merchantId: string;
  // currencySymbol kept for backward compatibility (passed down to AddItemsModal)
  currencySymbol?: string;
}

export default function BookingItemsEditor({
  booking,
  items,
  extensions = [],
  onChange,
  disabled,
  merchantId,
  currencySymbol
}: BookingItemsEditorProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [addModalKind, setAddModalKind] = useState<'products' | 'addons' | null>(null);
  const [changeAllocationItem, setChangeAllocationItem] = useState<BookingItemWithRelations | null>(null);
  const [reportIssueItem, setReportIssueItem] = useState<BookingItemWithRelations | null>(null);

  const handleRemoveItem = (itemId: string) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    onChange(updatedItems as BookingItem[]);
  };

  const handleAllocationChanged = (
    itemId: string,
    newInventoryItemId: string,
    newInventoryItemData: { nr: number | null; item_number: string | null; combination_code: string | null; size: string | null }
  ) => {
    // Update the item's inventory_item_id, inventory_items join data, and
    // the denormalized size field to match the new inventory item
    const updatedItems = items.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          inventory_item_id: newInventoryItemId,
          inventory_items: newInventoryItemData,
          size: newInventoryItemData.size
        };
      }
      return item;
    });
    onChange(updatedItems as BookingItem[]);
    setChangeAllocationItem(null);
  };

  const handleAddItem = (newItemData: {
    product_type_id: string;
    item_name: string;
    size: string | null;
    quantity: number;
    unit_price: number;
    inventory_item_id: string | null;
    inventory_type: 'individual' | 'pool' | 'unlimited';
    duration_hours: number;
    total_price: number;
    ruleColumns: ItemPricingRuleColumns;
    isBundle?: boolean;
    bundleComponents?: Array<{
      product_type_id: string;
      product_name: string;
      quantity: number;
      size: string | null;
      inventoryItemIds: string[];
      inventoryType: 'individual' | 'pool' | 'unlimited';
    }>;
  }) => {
    if (newItemData.isBundle && newItemData.bundleComponents) {
      // Create parent row
      const parentId = `temp-bundle-${Date.now()}`;
      const parentItem: any = {
        id: parentId,
        booking_id: booking.id,
        product_type_id: newItemData.product_type_id,
        item_name: newItemData.item_name,
        size: null,
        quantity: 1,
        unit_price: newItemData.unit_price,
        inventory_item_id: null,
        duration_hours: newItemData.duration_hours,
        total_price: newItemData.total_price,
        ...newItemData.ruleColumns,
        is_bundle_parent: true,
        parent_booking_item_id: null,
        code: null,
        features: null,
        product_type: 'bundle',
        created_at: new Date().toISOString(),
      };

      // Create child rows
      const childItems: any[] = [];
      for (const comp of newItemData.bundleComponents) {
        if (comp.inventoryType === 'individual') {
          for (const itemId of comp.inventoryItemIds) {
            childItems.push({
              id: `temp-comp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
              booking_id: booking.id,
              product_type_id: comp.product_type_id,
              item_name: comp.product_name,
              size: comp.size,
              quantity: 1,
              unit_price: 0,
              inventory_item_id: itemId,
              duration_hours: newItemData.duration_hours,
              total_price: 0,
              ...newItemData.ruleColumns,
              is_bundle_parent: false,
              parent_booking_item_id: parentId,
              code: null,
              features: null,
              product_type: 'bundle_component',
              created_at: new Date().toISOString(),
            });
          }
        } else {
          childItems.push({
            id: `temp-comp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            booking_id: booking.id,
            product_type_id: comp.product_type_id,
            item_name: comp.product_name,
            size: comp.size,
            quantity: comp.quantity,
            unit_price: 0,
            inventory_item_id: null,
            duration_hours: newItemData.duration_hours,
            total_price: 0,
            ...newItemData.ruleColumns,
            is_bundle_parent: false,
            parent_booking_item_id: parentId,
            code: null,
            features: null,
            product_type: 'bundle_component',
            created_at: new Date().toISOString(),
          });
        }
      }

      onChange([...items, parentItem, ...childItems] as BookingItem[]);
      return;
    }

    // Non-bundle: try to merge into an existing compatible row before
    // appending a new one. Merging is only safe for pool/unlimited
    // inventory where one row represents an aggregate quantity rather
    // than a specific serialized unit — individual items must stay on
    // their own rows so Change Allocation, per-item issue reports and
    // returns continue to work.
    const canMerge =
      (newItemData.inventory_type === 'pool' || newItemData.inventory_type === 'unlimited') &&
      newItemData.inventory_item_id == null;

    if (canMerge) {
      const mergeIndex = items.findIndex((existing: any) => (
        existing.product_type_id === newItemData.product_type_id &&
        (existing.size ?? null) === (newItemData.size ?? null) &&
        existing.duration_hours === newItemData.duration_hours &&
        existing.unit_price === newItemData.unit_price &&
        (existing.pricing_rule_id ?? null) === newItemData.ruleColumns.pricing_rule_id &&
        existing.inventory_item_id == null &&
        !existing.is_bundle_parent &&
        !existing.parent_booking_item_id
      ));

      if (mergeIndex !== -1) {
        const existing = items[mergeIndex] as any;
        const newQty = (existing.quantity || 1) + (newItemData.quantity || 1);
        const mergedItem = {
          ...existing,
          quantity: newQty,
          total_price: (existing.unit_price || 0) * newQty,
        };
        const updated = [...items];
        updated[mergeIndex] = mergedItem;
        onChange(updated as BookingItem[]);
        showToast(t('bookingItems.mergedQuantity', { name: existing.item_name, qty: newQty }), 'success', 2500);
        return;
      }
    }

    // No compatible row found (or merging not safe): append a new row.
    const newItem: any = {
      id: `temp-${Date.now()}`,
      booking_id: booking.id,
      product_type_id: newItemData.product_type_id,
      item_name: newItemData.item_name,
      size: newItemData.size,
      quantity: newItemData.quantity,
      unit_price: newItemData.unit_price,
      inventory_item_id: newItemData.inventory_item_id,
      duration_hours: newItemData.duration_hours,
      total_price: newItemData.total_price,
      ...newItemData.ruleColumns,
      is_bundle_parent: false,
      parent_booking_item_id: null,
      code: null,
      features: null,
      product_type: null,
      created_at: new Date().toISOString(),
    };

    onChange([...items, newItem] as BookingItem[]);
  };

  const formatPrice = (price: number | null) => {
    // Prefer robust formatting via booking.currency.
    // Fallback to a reasonable default if currency not present.
    if (booking?.currency) return formatCurrency(price, booking.currency);
    return formatCurrency(price, 'USD');
  };

  const itemsSubtotal = items.reduce((sum, item) => sum + (item.total_price || 0), 0);
  const extensionsSubtotal = extensions.reduce((sum, ext) => sum + (ext.total_price || 0), 0);

  // Group extensions by extension_batch_id, preserving event order via earliest created_at.
  const extensionBatches = (() => {
    const sorted = [...extensions].sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : 0;
      const tb = b.created_at ? Date.parse(b.created_at) : 0;
      return ta - tb;
    });
    const map = new Map<string, BookingExtension[]>();
    for (const ext of sorted) {
      const key = ext.extension_batch_id;
      const bucket = map.get(key);
      if (bucket) bucket.push(ext);
      else map.set(key, [ext]);
    }
    return Array.from(map.entries()).map(([batchId, rows]) => ({ batchId, rows }));
  })();

  const formatBatchHeader = (rows: BookingExtension[]) => {
    const first = rows[0];
    const appliedAt = first.created_at
      ? new Date(first.created_at).toLocaleString()
      : '—';
    const duration = first.duration_label || '';
    const from = first.extended_from_end_date
      ? new Date(first.extended_from_end_date).toLocaleString()
      : '—';
    const to = first.extended_to_end_date
      ? new Date(first.extended_to_end_date).toLocaleString()
      : '—';
    return t('bookingItems.extensionAppliedAt', { date: appliedAt, duration, from, to });
  };

  return (
    <>
      <div className="space-y-lg">
        <div className="flex flex-col gap-sm sm:flex-row sm:items-center sm:justify-between">
          <h3 className="text-lg font-semibold text-primary flex items-center gap-xs">
            <Package className="w-5 h-5 text-blue" />
            {t('bookingItems.title', { count: items.length })}
          </h3>
          <div className="flex items-center gap-sm flex-wrap">
            <button
              onClick={() => {
                if (disabled) return;
                setAddModalKind('products');
              }}
              disabled={disabled || !booking.start_date || !booking.end_date}
              className={`btn btn-primary ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              title={!booking.start_date || !booking.end_date ? t('bookingItems.setDatesFirst') : t('bookingItems.addItemsToBooking')}
              data-testid="booking-edit-add-item-button"
            >
              <Plus className="w-4 h-4" />
              {t('bookingItems.addItem')}
            </button>
            <button
              onClick={() => {
                if (disabled) return;
                setAddModalKind('addons');
              }}
              disabled={disabled || !booking.start_date || !booking.end_date}
              className={`btn btn-ghost ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
              title={!booking.start_date || !booking.end_date ? t('bookingItems.setDatesFirst') : t('bookingItems.addAddonToBooking')}
              data-testid="booking-edit-add-addon-button"
            >
              <Plus className="w-4 h-4" />
              {t('bookingItems.addAddon')}
            </button>
          </div>
        </div>

      {items.length === 0 ? (
        <div className="text-center py-2xl border-2 border-dashed border-secondary rounded-lg">
          <Package className="w-12 h-12 text-secondary mx-auto mb-md" />
          <h4 className="text-lg font-semibold text-primary mb-xs">{t('bookingItems.noItems')}</h4>
          <p className="text-secondary text-sm">
            {t('bookingItems.noItemsDescription')}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary border-b border-primary">
              <tr>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.itemNumber')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.comboCode')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.itemName')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.size')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.qty')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.unitPrice')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.duration')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.total')}</th>
                <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((item) => (
                <tr key={item.id} className="hover:bg-secondary">
                  <td className="py-md px-lg">
                    <span className="text-sm font-medium text-primary">
                      {item.inventory_items?.item_number || t('common.na')}
                    </span>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm font-mono text-secondary">
                      {item.code || '-'}
                    </span>
                  </td>
                  <td className="py-md px-lg">
                    <div>
                      <div className="flex items-center gap-xs flex-wrap">
                        <p className="text-sm font-medium text-primary">{item.item_name}</p>
                        {item.product_types?.product_type === 'addon' && (
                          <span className="px-xs py-0.5 text-xxs rounded bg-blue/10 text-blue border border-blue/30">
                            {t('bookingItems.addonBadge')}
                          </span>
                        )}
                        {((item.product_types?.location_id && item.product_types.location_id !== booking.location_id) ||
                          (item.inventory_items?.product_types?.location_id && item.inventory_items.product_types.location_id !== booking.location_id)) && (
                          <span title={`${t('bookingItems.homeLocation')}: ${item.inventory_items?.product_types?.locations?.location_name || item.product_types?.locations?.location_name || t('bookingItems.anotherLocation')} — ${t('bookingItems.transferRequired')}`}>
                            <Truck className="w-4 h-4 text-danger flex-shrink-0" />
                          </span>
                        )}
                      </div>
                      {item.product_types?.product_type && item.product_types.product_type !== 'addon' && (
                        <p className="text-xs text-secondary">{item.product_types.product_type}</p>
                      )}
                    </div>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm text-secondary">{item.size || '-'}</span>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm text-primary">{item.quantity || 1}</span>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm text-primary">{formatPrice(item.unit_price)}</span>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm text-secondary">{item.duration_hours}h</span>
                  </td>
                  <td className="py-md px-lg">
                    <span className="text-sm font-medium text-primary">
                      {formatPrice(item.total_price)}
                    </span>
                  </td>
                  <td className="py-md px-lg">
                    <div className="flex items-center gap-xs">
                      {/* Change Allocation button - only for individual items in open bookings (not unsaved items) */}
                      {item.inventory_item_id && !item.id.startsWith('temp-') && (
                        <button
                          onClick={() => setChangeAllocationItem(item)}
                          disabled={disabled}
                          className={`btn-icon ${
                            disabled
                              ? 'text-secondary/40 cursor-not-allowed'
                              : 'text-blue hover:bg-blue/10'
                          }`}
                          title={disabled ? t('bookingItems.editingDisabled') : t('bookingItems.changeAllocation')}
                          data-testid={`booking-edit-change-allocation-${item.id}-button`}
                        >
                          <ArrowRightLeft className="w-4 h-4" />
                        </button>
                      )}
                      {/* Report Issue button */}
                      <button
                        onClick={() => setReportIssueItem(item)}
                        className="btn-icon text-warning hover:bg-warning/10"
                        title={t('bookingItems.reportIssueForItem')}
                      >
                        <AlertTriangle className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        disabled={disabled}
                        className={`btn-icon ${
                          disabled
                            ? 'text-secondary/40 cursor-not-allowed'
                            : 'text-danger hover:bg-danger/10'
                        }`}
                        title={disabled ? t('bookingItems.editingDisabled') : t('bookingItems.removeItem')}
                        data-testid={`booking-edit-remove-item-${item.id}-button`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Extensions Section */}
      {extensionBatches.length > 0 && (
        <div className="space-y-md">
          <div className="flex items-center gap-xs">
            <Clock className="w-5 h-5 text-blue" />
            <h4 className="text-md font-semibold text-primary">
              {t('bookingItems.extensions')}
            </h4>
            <span className="text-xs text-secondary">
              {t('bookingItems.extensionsDescription')}
            </span>
          </div>
          {extensionBatches.map(({ batchId, rows }) => (
            <div key={batchId} className="border border-primary rounded-lg overflow-hidden">
              <div className="bg-secondary px-lg py-sm text-xs font-medium text-secondary border-b border-primary">
                {formatBatchHeader(rows)}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary border-b border-primary">
                    <tr>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.itemName')}</th>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.size')}</th>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.qty')}</th>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.unitPrice')}</th>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.duration')}</th>
                      <th className="text-left py-md px-lg text-sm font-semibold text-primary">{t('bookingItems.total')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {rows.map((ext) => (
                      <tr key={ext.id} className="hover:bg-secondary">
                        <td className="py-md px-lg">
                          <span className="text-sm font-medium text-primary">{ext.item_name || '-'}</span>
                        </td>
                        <td className="py-md px-lg">
                          <span className="text-sm text-secondary">{ext.size || '-'}</span>
                        </td>
                        <td className="py-md px-lg">
                          <span className="text-sm text-primary">{ext.quantity ?? 1}</span>
                        </td>
                        <td className="py-md px-lg">
                          <span className="text-sm text-primary">{formatPrice(ext.unit_price)}</span>
                        </td>
                        <td className="py-md px-lg">
                          <span className="text-sm text-secondary">{ext.duration_label || '-'}</span>
                        </td>
                        <td className="py-md px-lg">
                          <span className="text-sm font-medium text-primary">{formatPrice(ext.total_price)}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Totals Summary */}
      {(items.length > 0 || extensions.length > 0) && (
        <div className="border-t border-primary pt-md">
          <div className="flex justify-end">
            <div className="w-72">
              {items.length > 0 && (
                <div className="flex justify-between text-sm mb-xs">
                  <span className="text-secondary">{t('bookingItems.itemsSubtotal')}:</span>
                  <span className="font-medium text-primary">{formatPrice(itemsSubtotal)}</span>
                </div>
              )}
              {extensions.length > 0 && (
                <div className="flex justify-between text-sm mb-xs">
                  <span className="text-secondary">{t('bookingItems.extensionsSubtotal')}:</span>
                  <span className="font-medium text-primary">{formatPrice(extensionsSubtotal)}</span>
                </div>
              )}
              {extensions.length > 0 && items.length > 0 && (
                <div className="flex justify-between text-sm mb-xs border-t border-primary pt-xs">
                  <span className="font-semibold text-primary">{t('bookingItems.combinedTotal')}:</span>
                  <span className="font-semibold text-primary">{formatPrice(itemsSubtotal + extensionsSubtotal)}</span>
                </div>
              )}
              <div className="text-xs text-secondary">
                {t('bookingItems.subtotalDescription')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bundle Items Info */}
      {items.some(item => item.is_bundle_parent || item.parent_booking_item_id) && (
        <div className="p-md bg-blue/10 border-l-4 border-blue rounded-lg">
          <div className="flex items-start gap-md">
            <AlertTriangle className="w-5 h-5 text-blue flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-blue mb-xs">{t('bookingItems.bundleItems')}</h4>
              <p className="text-sm text-secondary">
                {t('bookingItems.bundleItemsDescription')}
              </p>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Add Items Modal */}
      {booking.start_date && booking.end_date && booking.location_id && (
        <AddItemsModal
          isOpen={addModalKind !== null}
          onClose={() => setAddModalKind(null)}
          locationId={booking.location_id}
          merchantId={merchantId}
          startDate={booking.start_date}
          endDate={booking.end_date}
          bookingId={booking.id}
          existingItems={items.map(i => ({
            inventory_item_id: i.inventory_item_id,
            product_type_id: i.product_type_id,
            quantity: i.quantity,
            is_bundle_parent: i.is_bundle_parent,
          }))}
          currencySymbol={currencySymbol}
          kind={addModalKind ?? 'products'}
          onAddItem={handleAddItem}
        />
      )}

      {/* Change Allocation Modal */}
      {changeAllocationItem && booking.start_date && booking.end_date && booking.location_id && (
        <ChangeAllocationModal
          isOpen={!!changeAllocationItem}
          onClose={() => setChangeAllocationItem(null)}
          bookingId={booking.id}
          bookingItemId={changeAllocationItem.id}
          currentInventoryItemId={changeAllocationItem.inventory_item_id}
          productTypeId={changeAllocationItem.product_type_id}
          productName={changeAllocationItem.item_name || changeAllocationItem.product_types?.product_name || t('common.unknown')}
          size={changeAllocationItem.size}
          locationId={booking.location_id}
          startDate={booking.start_date}
          endDate={booking.end_date}
          merchantId={merchantId}
          isTransferItem={!!(
            (changeAllocationItem.product_types?.location_id && changeAllocationItem.product_types.location_id !== booking.location_id) ||
            (changeAllocationItem.inventory_items?.product_types?.location_id && changeAllocationItem.inventory_items.product_types.location_id !== booking.location_id)
          )}
          homeLocationName={changeAllocationItem.inventory_items?.product_types?.locations?.location_name || changeAllocationItem.product_types?.locations?.location_name}
          siblingInventoryItemIds={
            items
              .filter(i => i.id !== changeAllocationItem.id && i.inventory_item_id)
              .map(i => i.inventory_item_id!)
          }
          onAllocationChanged={(newInventoryItemId, newItemData) => handleAllocationChanged(changeAllocationItem.id, newInventoryItemId, newItemData)}
        />
      )}

      {/* Report Issue Modal */}
      {reportIssueItem && (
        <ReportIssueModal
          isOpen={!!reportIssueItem}
          onClose={() => setReportIssueItem(null)}
          onIssueCreated={() => setReportIssueItem(null)}
          initialData={{
            merchant_id: merchantId,
            location_id: booking.location_id || null,
            inventory_item_id: reportIssueItem.inventory_item_id || null,
            product_type_id: reportIssueItem.product_type_id || null,
            booking_id: booking.id,
            source: 'internal',
            item_label: `${reportIssueItem.item_name || 'Item'} ${reportIssueItem.inventory_items?.item_number || ''}`.trim(),
          }}
        />
      )}
    </>
  );
}
